/*
  Home page behaviour:

  1. Off/On switches ([data-mh-switch]). Clicking flips the target ([data-mh-flip]).
     Switches marked data-auto flip themselves every few seconds while on screen, and stop
     for good once someone clicks one, so a visitor is never fighting the animation.

  2. The price switch ([data-mh-price]) toggles data-show between "month" and "once".
  3. Tabs ([data-mv-tabs]) and the hero window's Download nudge.
  4. Count-up numbers ([data-count-to]).
  5. Rotating headline word ([data-mv-rotate]).
  6. Previous/next buttons for scroll rows ([data-mv-scroll]).
  7. The hero window demo ([data-demo]).
  8. Monthly / Yearly toggle on pricing ([data-mv-billing]).
*/
(function () {
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setSwitch(sw, on) {
    sw.setAttribute('aria-checked', on ? 'true' : 'false');
    var target = document.getElementById(sw.getAttribute('aria-controls'));
    if (target) { target.classList.toggle('is-on', on); }
    var price = sw.closest('[data-mh-price]');
    if (price) { price.setAttribute('data-show', on ? 'once' : 'month'); }
  }

  function wire(root) {
    var switches = root.querySelectorAll('[data-mh-switch]');
    Array.prototype.forEach.call(switches, function (sw) {
      if (sw.dataset.wired) { return; }
      sw.dataset.wired = '1';
      sw.addEventListener('click', function () {
        sw.dataset.touched = '1';
        setSwitch(sw, sw.getAttribute('aria-checked') !== 'true');
      });

      if (reduced || !sw.hasAttribute('data-auto')) { return; }
      var delay = parseInt(sw.getAttribute('data-auto'), 10) || 2600;
      var timer = null;
      var visible = false;

      function tick() {
        if (!visible || sw.dataset.touched) { return; }
        setSwitch(sw, sw.getAttribute('aria-checked') !== 'true');
        timer = setTimeout(tick, delay);
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          clearTimeout(timer);
          if (visible) { timer = setTimeout(tick, delay); }
        }, { threshold: 0.35 }).observe(sw.closest('section, .mh') || sw);
      }
    });
  }

  /* 3. Tabs ([data-mv-tabs]). Auto-advances every 4.5s while on screen, stops once clicked. */
  function wireTabs(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-mv-tabs]'), function (box) {
      if (box.dataset.wired) { return; }
      box.dataset.wired = '1';
      var tabs = box.querySelectorAll('[role="tab"]');
      var touched = false;
      var visible = false;
      var i = 0;

      function show(n) {
        i = (n + tabs.length) % tabs.length;
        Array.prototype.forEach.call(tabs, function (t, k) {
          var on = k === i;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) { panel.classList.toggle('is-active', on); }
        });
      }

      Array.prototype.forEach.call(tabs, function (t, k) {
        t.addEventListener('click', function () { touched = true; show(k); });
        t.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { touched = true; show(i + 1); tabs[i].focus(); e.preventDefault(); }
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { touched = true; show(i - 1); tabs[i].focus(); e.preventDefault(); }
        });
      });

      if (reduced || !('IntersectionObserver' in window)) { return; }
      new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; }, { threshold: 0.4 }).observe(box);
      setInterval(function () { if (visible && !touched) { show(i + 1); } }, 4500);
    });
  }

  /* The hero window nudges its Download button while the render is showing. */
  function wireWindow(root) {
    Array.prototype.forEach.call(root.querySelectorAll('.mv-window'), function (w) {
      var flip = w.querySelector('.mh-flip');
      if (!flip || w.dataset.wired) { return; }
      w.dataset.wired = '1';
      new MutationObserver(function () { w.classList.toggle('is-on', flip.classList.contains('is-on')); }).observe(flip, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* 4. Count-up ([data-count-to]). The real number is already in the HTML, so no-JS and
        reduced-motion visitors see it straight away. Counts from 90% up when it scrolls into view. */
  function wireCounts(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-count-to]'), function (el) {
      if (el.dataset.wired) { return; }
      el.dataset.wired = '1';
      var target = parseInt(el.getAttribute('data-count-to'), 10);
      if (!target || reduced || !('IntersectionObserver' in window)) { return; }
      var fmt = function (n) { return Math.round(n).toLocaleString('en-AU'); };
      var from = Math.floor(target * 0.9);
      /* The real number stays in the page until the count actually starts, so if the observer
         never fires (hidden tab, odd browser) nobody sees a wrong figure. */
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) { return; }
        io.disconnect();
        el.textContent = fmt(from);
        /* Timer, not requestAnimationFrame: rAF pauses in background tabs, which could leave
           the wrong number showing. Time-based, so it always lands on the real figure. */
        var start = Date.now(), dur = 1800;
        var timer = setInterval(function () {
          var p = Math.min((Date.now() - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 4);
          el.textContent = fmt(from + (target - from) * eased);
          if (p >= 1) { clearInterval(timer); el.textContent = fmt(target); }
        }, 30);
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  /* 5. Rotating word ([data-mv-rotate]). Cycles its <em>s every 2.2s. First word only under reduced motion. */
  function wireRotate(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-mv-rotate]'), function (box) {
      if (box.dataset.wired || reduced) { return; }
      box.dataset.wired = '1';
      var words = box.querySelectorAll('em');
      if (words.length < 2) { return; }
      var i = 0;
      setInterval(function () {
        var out = words[i];
        i = (i + 1) % words.length;
        out.classList.remove('is-on'); out.classList.add('is-out');
        words[i].classList.remove('is-out'); words[i].classList.add('is-on');
        setTimeout(function () { out.classList.remove('is-out'); }, 600);
      }, 2200);
    });
  }

  /* 6. Scroll rows ([data-mv-scroll] buttons). Move one card at a time; greyed out at each end. */
  function wireScroll(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-mv-scroll]'), function (btn) {
      if (btn.dataset.wired) { return; }
      btn.dataset.wired = '1';
      var row = document.getElementById(btn.getAttribute('aria-controls'));
      if (!row) { return; }
      btn.addEventListener('click', function () {
        var card = row.firstElementChild;
        var step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(row).columnGap || 0) : 300;
        row.scrollBy({ left: step * parseInt(btn.getAttribute('data-mv-scroll'), 10), behavior: reduced ? 'auto' : 'smooth' });
      });
      function update() {
        var dir = parseInt(btn.getAttribute('data-mv-scroll'), 10);
        var max = row.scrollWidth - row.clientWidth - 2;
        btn.disabled = dir < 0 ? row.scrollLeft <= 2 : row.scrollLeft >= max;
      }
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* 7. Hero window demo ([data-demo]). A cursor picks up the logo file, drops it on the photo,
        the real render appears, it clicks Download, then moves to the next photo. Loops while
        on screen. Stops for good if someone uses the Off/On switch. Reduced motion: shows the
        finished render and does nothing else. */
  function wireDemo(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-demo]'), function (win) {
      if (win.dataset.demoWired) { return; }
      win.dataset.demoWired = '1';
      var scenes;
      try { scenes = JSON.parse(win.getAttribute('data-demo-scenes')); } catch (e) { return; }
      var q = function (sel) { return win.querySelector(sel); };
      var flip = q('.mh-flip'), imgs = flip ? flip.querySelectorAll('img') : [];
      var sw = q('[data-mh-switch]'), cursor = q('[data-demo-cursor]'), chip = q('[data-demo-chip]');
      var status = q('[data-demo-status]'), toast = q('[data-demo-toast]'), dl = q('[data-demo-download]');
      var logo = q('[data-demo-logo]'), nameEl = q('[data-demo-name]');
      var badge = q('[data-demo-logo-badge]'), logoName = q('[data-demo-logo-name]'), logoMeta = q('[data-demo-logo-meta]'), chipImg = q('[data-demo-chip-img]');
      /* A scene with "design" shows that artwork as the file and drags it, instead of the plain "your logo" chip. */
      function setDesign(sc) {
        var has = !!sc.design;
        if (badge) { badge.classList.toggle('has-thumb', has); badge.innerHTML = has ? '<img alt="" src="' + sc.design + '">' : 'PNG'; }
        if (logoName) { logoName.textContent = has ? sc.file : 'your-logo.png'; }
        if (logoMeta) { logoMeta.textContent = has ? 'your design' : 'transparent background'; }
        if (chip) { chip.classList.toggle('has-img', has); }
        if (chipImg && has) { chipImg.src = sc.design; }
      }
      if (!flip || imgs.length < 2 || !scenes || !scenes.length) { return; }

      if (reduced) { flip.classList.add('is-on'); if (sw) { sw.setAttribute('aria-checked', 'true'); } return; }

      scenes.forEach(function (sc) { new Image().src = sc.off; new Image().src = sc.on; if (sc.design) { new Image().src = sc.design; } });
      setDesign(scenes[0]);

      var stopped = false, visible = false, running = false, timers = [], i = 0;
      if (sw) { sw.addEventListener('click', function () { stopped = true; clear(); hideBits(); }); }

      function at(ms, fn) { timers.push(setTimeout(function () { if (!stopped) { fn(); } }, ms)); }
      function clear() { timers.forEach(clearTimeout); timers = []; running = false; }
      function hideBits() {
        cursor.style.opacity = 0; chip.classList.remove('is-held', 'is-dropped');
        status.classList.remove('is-on'); toast.classList.remove('is-on');
        if (logo) { logo.classList.remove('is-picked'); }
      }
      function point(el, dx, dy) {
        /* Relative to the element the cursor and chip are positioned in (the canvas), not the window. */
        var w = (cursor.offsetParent || win).getBoundingClientRect(), r = el.getBoundingClientRect();
        return { x: r.left - w.left + r.width * (dx == null ? .5 : dx), y: r.top - w.top + r.height * (dy == null ? .5 : dy) };
      }
      function place(el, p, ox, oy) { el.style.setProperty('--x', (p.x + (ox || 0)) + 'px'); el.style.setProperty('--y', (p.y + (oy || 0)) + 'px'); }
      function setStatus(text, cls) { status.textContent = text; status.className = 'mv-demo__status is-on ' + (cls || ''); }
      function setSwitch(on) { flip.classList.toggle('is-on', on); if (sw) { sw.setAttribute('aria-checked', on ? 'true' : 'false'); } }

      function play() {
        if (stopped || !visible || running) { return; }
        running = true;
        var sc = scenes[i % scenes.length];
        var sideShown = logo && logo.offsetParent !== null;
        var canvas = q('.mv-canvas');

        /* 0: new blank photo */
        setSwitch(false);
        flip.classList.add('is-swapping');
        at(250, function () {
          imgs[0].src = sc.off; imgs[1].src = sc.on;
          if (nameEl) { nameEl.textContent = sc.name; }
          setDesign(sc);
          flip.classList.remove('is-swapping');
          setStatus('Pick a template');
        });

        /* 1: cursor to the logo file (or up from the bottom on phones) */
        var start = sideShown ? point(logo, .3, .5) : point(canvas, .5, 1.05);
        at(700, function () {
          cursor.style.transition = 'none'; place(cursor, sideShown ? point(canvas, .2, .9) : start, 0, 30);
          void cursor.offsetWidth; cursor.style.transition = ''; cursor.style.opacity = 1;
          place(cursor, start);
        });
        at(1600, function () {
          cursor.classList.add('is-down');
          if (logo) { logo.classList.add('is-picked'); }
          chip.style.transition = 'none'; place(chip, start, 12, 14); void chip.offsetWidth; chip.style.transition = '';
          chip.classList.remove('is-dropped'); chip.classList.add('is-held');
          setStatus('Adding your logo', 'is-busy');
        });

        /* 2: drag onto the photo and drop */
        var target = point(flip, .5, .5);
        at(1900, function () { place(cursor, target); place(chip, target, 12, 14); });
        at(2900, function () {
          cursor.classList.remove('is-down');
          if (logo) { logo.classList.remove('is-picked'); }
          chip.classList.add('is-dropped');
          setStatus('Placing it on the photo', 'is-busy');
        });
        at(3350, function () {
          setSwitch(true);
          flip.classList.remove('is-flash'); void flip.offsetWidth; flip.classList.add('is-flash');
          setStatus('Done', 'is-done');
          chip.classList.remove('is-held');
        });

        /* 3: download */
        var dlp = dl && dl.offsetParent !== null ? point(dl, .5, .6) : null;
        at(4300, function () { if (dlp) { place(cursor, dlp); } });
        at(5250, function () { if (dlp) { cursor.classList.add('is-down'); dl.classList.add('is-pressed'); } });
        at(5450, function () { cursor.classList.remove('is-down'); if (dl) { dl.classList.remove('is-pressed'); } toast.classList.add('is-on'); });

        /* 4: hand over to the next photo */
        at(7400, function () { toast.classList.remove('is-on'); status.classList.remove('is-on'); cursor.style.opacity = 0; });
        at(8000, function () { running = false; i++; play(); });
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) { play(); } else { clear(); hideBits(); }
        }, { threshold: 0.45 }).observe(win);
      }
    });
  }

  /* 8. Billing toggle on pricing ([data-mv-billing]). Sets data-show to monthly or yearly. */
  function wireBilling(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-mv-billing]'), function (box) {
      if (box.dataset.wired) { return; }
      box.dataset.wired = '1';
      var btns = box.querySelectorAll('[data-billing-set]');
      Array.prototype.forEach.call(btns, function (btn) {
        btn.addEventListener('click', function () {
          var v = btn.getAttribute('data-billing-set');
          box.setAttribute('data-show', v);
          Array.prototype.forEach.call(btns, function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        });
      });
    });
  }


  /* 9. Hero studio demo ([data-pack-demo], snippets/me-pack-demo.liquid): the cursor drops a design into the rail,
        taps four templates, each shimmers and comes back branded and lands in the tray, then Download all.
        Alternates scenes. Plays only while on screen. Reduced motion: shows the first grid and does nothing else. */
  function wirePackDemo(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-pack-demo]'), function (win) {
      if (win.dataset.wired) { return; }
      win.dataset.wired = '1';
      var scenes; try { scenes = JSON.parse(win.getAttribute('data-scenes')); } catch (e) { return; }
      if (!scenes || !scenes.length || reduced) { return; }
      var q = function (s) { return win.querySelector(s); };
      var cursor = q('[data-sd-cursor]'), drop = q('[data-sd-drop]'), design = q('[data-sd-design]'), tray = q('[data-sd-tray]');
      var count = q('[data-sd-count]'), zip = q('[data-sd-zip]'), toast = q('[data-sd-toast]');
      var tiles = win.querySelectorAll('.mv-sd__tile');
      var timers = [], visible = false, running = false, i = 0;
      scenes.forEach(function (sc) { new Image().src = sc.design; sc.tiles.forEach(function (t) { new Image().src = t.blank; if (t.done) { new Image().src = t.done; } }); });

      function at(ms, fn) { timers.push(setTimeout(fn, ms)); }
      function clear() { timers.forEach(clearTimeout); timers = []; running = false; }
      function shown(el) { return el && el.offsetParent !== null; }
      function point(el, dx, dy) {
        var w = win.getBoundingClientRect(), r = el.getBoundingClientRect();
        return { x: r.left - w.left + r.width * (dx == null ? .5 : dx), y: r.top - w.top + r.height * (dy == null ? .5 : dy) };
      }
      function move(p) { cursor.style.setProperty('--x', p.x + 'px'); cursor.style.setProperty('--y', p.y + 'px'); }
      function tap(el) {
        cursor.classList.add('is-down'); if (el) { el.classList.add('is-pressed'); }
        at(180, function () { cursor.classList.remove('is-down'); if (el) { el.classList.remove('is-pressed'); } });
      }

      function play() {
        if (!visible || running) { return; }
        running = true;
        var sc = scenes[i % scenes.length];
        /* reset to blank */
        drop.classList.remove('has-file'); design.src = sc.design;
        tray.innerHTML = ''; count.textContent = '0'; toast.classList.remove('is-on');
        Array.prototype.forEach.call(tiles, function (tl, n) {
          tl.classList.remove('is-busy', 'is-yours', 'is-hover');
          var t = sc.tiles[n]; if (t) { tl.querySelector('img').src = t.blank; }
        });
        cursor.style.transition = 'none'; move(point(win, .5, 1.05)); void cursor.offsetWidth; cursor.style.transition = ''; cursor.style.opacity = 1;

        var t = 500;
        if (shown(drop)) {
          at(t, function () { move(point(drop, .5, .5)); });
          at(t += 950, function () { tap(); drop.classList.add('has-file'); });
        } else {
          at(t, function () { drop.classList.add('has-file'); });
        }
        var picks = [];
        sc.tiles.forEach(function (tt, n) { if (tt.done && tiles[n]) { picks.push(n); } });
        picks.slice(0, 4).forEach(function (n, k) {
          var tl = tiles[n];
          at(t += (k ? 700 : 800), function () { move(point(tl, .5, .5)); tl.classList.add('is-hover'); });
          at(t += 850, function () { tap(); tl.classList.remove('is-hover'); tl.classList.add('is-busy'); });
          at(t += 1100, function () {
            var img = tl.querySelector('img'); img.src = sc.tiles[n].done;
            tl.classList.remove('is-busy'); tl.classList.remove('is-yours'); void tl.offsetWidth; tl.classList.add('is-yours');
            var th = document.createElement('i'); th.style.backgroundImage = 'url("' + sc.tiles[n].done + '")'; tray.insertBefore(th, tray.firstChild);
            count.textContent = String(k + 1);
          });
        });
        if (shown(zip)) {
          at(t += 800, function () { move(point(zip, .5, .6)); });
          at(t += 950, function () { tap(zip); toast.classList.add('is-on'); });
        } else {
          at(t += 800, function () { toast.classList.add('is-on'); });
        }
        at(t += 2300, function () { toast.classList.remove('is-on'); cursor.style.opacity = 0; });
        at(t += 700, function () { running = false; i++; play(); });
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) { play(); } else { clear(); cursor.style.opacity = 0; }
        }, { threshold: 0.35 }).observe(win);
      }
    });
  }

  function run() { wire(document); wireTabs(document); wireWindow(document); wireCounts(document); wireRotate(document); wireScroll(document); wireDemo(document); wirePackDemo(document); wireBilling(document); }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', run); } else { run(); }
  document.addEventListener('shopify:section:load', function (e) { wire(e.target); wireTabs(e.target); wireWindow(e.target); wireCounts(e.target); wireRotate(e.target); wireScroll(e.target); wireDemo(e.target); wirePackDemo(e.target); wireBilling(e.target); });
})();
